/* ---------------------------------------------------------------------------
   "Delete this model?" — the one way back to a clean sheet.

   A model built from the wrong template, or from geometry that was later
   corrected on the record, is not worth editing into shape: the parameter panel
   cannot turn a three-span overpass into a truss. The way out is to remove what
   is there and start again from the picker, which is what this dialog does in
   one step — see the `thenNew` path in store.ts's deleteAllModels.

   Confirmed rather than done on the click, and listed rather than counted,
   because the delete reaches further than the geometry. Everything the modeller
   keyed to the model goes with it: the defect pins an inspector placed on the 3D
   view, the photographs attached to those pins, and the BOQ rates and carbon
   factors set for that model. None of it is copied anywhere else, and the
   modeller has no undo across a session. So the dialog says what goes and what
   stays before it offers the button.

   Rendered into src/main.ts's dialog layer alongside the picker it hands over
   to — see the note on the dialog layer in src/main.ts for why it exists.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { actions, type State } from '../core/store.ts';
import { button, scrim } from '../components/widgets.ts';
import { findBridge, prettyDate } from '../data/derive.ts';
import type { ModelSummary } from '../data/types.ts';

/**
 * A model's size, in the unit that makes it a number a person can hold.
 *
 * Null where nothing has been saved yet — a model created seconds ago, before
 * its first autosave — which is "not yet saved", not zero bytes.
 */
function sizeLabel(bytes: number | null): string {
  if (bytes == null) return 'not yet saved';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** One model up for deletion, with the row's own delete for a list of several. */
function row(model: ModelSummary, s: Readonly<State>, alone: boolean): HTMLElement {
  return h(
    'div',
    { class: 'mdel__row' },
    h(
      'div',
      { class: 'mdel__what' },
      h('div', { class: 'mdel__name', text: model.name }),
      h('div', {
        class: 'mdel__meta',
        text: [
          model.templateId || 'no template recorded',
          sizeLabel(model.sizeBytes),
          model.updatedAt ? `last saved ${prettyDate(new Date(model.updatedAt).toISOString())}` : null,
        ]
          .filter(Boolean)
          .join(' · '),
      }),
    ),
    h('span', { class: 'spacer' }),
    /* Only when there is a choice to make. With one model on the structure this
       button and the footer's would do exactly the same thing, and offering the
       same delete twice is how someone ends up unsure which one they pressed. */
    alone
      ? null
      : button('Delete', {
          sm: true,
          danger: true,
          disabled: s.modelDeleteBusy,
          iconPath: ICONS.trash,
          title: `Delete "${model.name}" and leave the others`,
          onClick: () => void actions.deleteModel(model.id),
        }),
  );
}

/** The confirmation, or null when it is closed. */
export function modelDeleteDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.modelDeleteBridgeId) return null;

  const b = findBridge(s.modelDeleteBridgeId);
  /* No record to hang this on, which happens only where the register is
     empty. Closed rather than drawn over nothing — the same answer this
     dialog already gives when the thing it was opened for has gone. */
  if (b === null) return null;
  const models = s.models.models;
  const count = models.length;

  // The last model can have gone while this was open — deleted from the
  // modeller on another device, or by the row buttons above. Nothing left to
  // confirm, so nothing to show.
  if (count === 0) return null;

  const alone = count === 1;

  return h(
    'div',
    {
      class: 'dlg',
      // Dismissable at the scrim like every other overlay here, except while the
      // delete is running — see scrim() and closeModelDelete().
      ...scrim(() => actions.closeModelDelete(), () => s.modelDeleteBusy),
    },
    h(
      'div',
      {
        class: 'dlg__panel dlg__panel--sm',
        role: 'dialog',
        'aria-modal': 'true',
        'aria-label': alone ? 'Delete this 3D model' : 'Delete these 3D models',
      },

      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · ${b.name}` }),
          h('h2', {
            class: 'dlg__title',
            text: alone ? 'Delete this 3D model?' : `Delete ${count} 3D models?`,
          }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            disabled: s.modelDeleteBusy || undefined,
            title: 'Cancel (Esc)',
            'aria-label': 'Cancel',
            onclick: () => actions.closeModelDelete(),
          },
          '×',
        ),
      ),

      /* What goes, said plainly — and it is less than it used to be. Defects were
         part of the model's own state until migration 0015; deleting the geometry
         took the survey recorded on it, which is field work rather than a drawing
         that can be redrawn. They are rows in the register now and they stay,
         which is worth saying outright: an inspector remembers the pins being in
         the 3D view, and would reasonably assume they went with it. */
      h(
        'div',
        { class: 'dlg__note dlg__note--error' },
        icon(ICONS.trash, { size: 13, stroke: 'currentColor' }),
        h('span', {
          text:
            'The geometry goes, along with the BOQ rates set for it. This cannot be undone.',
        }),
      ),

      h('div', { class: 'mdel__list' }, ...models.map((m) => row(m, s, alone))),

      s.modelDeleteError
        ? h('div', { class: 'dlg__note dlg__note--error', text: s.modelDeleteError })
        : null,

      h(
        'div',
        { class: 'dlg__foot' },
        h('span', {
          class: 'dlg__foot-note',
          text: s.modelDeleteBusy
            ? 'Deleting — this is several writes per model, so give it a moment.'
            : 'Starting fresh opens the bridge-type picker, pre-filled from this record.',
        }),
        h('span', { class: 'spacer' }),
        button('Cancel', {
          disabled: s.modelDeleteBusy,
          onClick: () => actions.closeModelDelete(),
        }),
        button(alone ? 'Delete' : `Delete all ${count}`, {
          danger: true,
          disabled: s.modelDeleteBusy,
          iconPath: ICONS.trash,
          title: 'Delete and return to the record',
          onClick: () => void actions.deleteAllModels(b.id),
        }),
      ),
    ),
  );
}
